/* eslint-disable no-restricted-syntax */

/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';

import _ from 'lodash';
import { compose } from 'recompose';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import type { ActionContext } from 'js/lib/ActionContext';

import { Checkbox, CheckboxGroup, Typography2 } from '@coursera/cds-core';

import { compareByNaturalSortOrder } from 'bundles/author-common/utils/common';
import PartnerHelpLink from 'bundles/authoring/common/components/PartnerHelpLink';
import { getRolesToOmit } from 'bundles/staff-management/src/store/constants';

import _t from 'i18n!nls/staff-management';

import 'css!./__styles__/RolesCheckboxGroup';

import type { ObjectId, StaffManagementStoreV2 as StaffManagementStoreClass, StaffMembershipType } from './store';
import { actions } from './store';
import { getRoles } from './utils/roleUtils';
import withFluxibleContextAsProps from './withFluxibleContextAsProps';
import withLoadingIndicator from './withLoadingIndicator';

type OptionInterface = { label: string; id: string };

type Props = {
  disabled?: boolean;
  /** Id of entity to load data from. */
  fromId?: ObjectId;

  /** Whether we are adding or editing members' roles. Modifies messaging shown.  */
  action: string;
  /** Callback to handle click, presecribed by CheckboxGroup. */
  onChange(options: Array<string>): void;
  /** List of roles available. */
  options?: Array<OptionInterface>;
  /** One of the types supported by the staff-membershp component. Modifies messaging shown. */
  type?: keyof typeof StaffMembershipType;
  /** The list of role ids currently chosen/checked. */
  value?: Array<string>;
};

type State = {
  rolesArray: Array<string>;
};

const styles = {
  rolesCheckboxTitle: {
    legend: {
      border: 'none',
    },
  },
};

/**
 * Roles selection component for the add and edit flows.
 *
 * Implementor provides onChange callback and set of values currently checked.
 */
export class RolesCheckboxGroup extends React.Component<Props, State> {
  static defaultProps = {
    disabled: false,
    load: _.noop,
    onChange: _.noop,
    options: [],
    value: [],
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      rolesArray: props.value || [],
    };
  }

  componentDidMount() {
    _.result(this.props, 'load');
  }

  static e2eName = 'staff-management-roles-checkbox-group';

  addRole = (role: string) => {
    const { rolesArray } = this.state;
    return [...rolesArray, role];
  };

  removeRole = (role: string) => {
    const { rolesArray } = this.state;

    const index = rolesArray.indexOf(role);
    const newValues = [...rolesArray];
    newValues.splice(index, 1);
    return [...newValues];
  };

  onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { onChange } = this.props;
    const { rolesArray } = this.state;
    const role = event.target.value;
    let newArray;
    if (rolesArray.includes(role)) {
      newArray = this.removeRole(event.target.value);
    } else {
      newArray = this.addRole(event.target.value);
    }

    this.setState({ rolesArray: newArray });
    onChange(newArray);
  };

  render() {
    const { options, type, action } = this.props;
    const { rolesArray } = this.state;
    // Certain roles may need to be hidden from the the user while under development
    const roles = getRoles();
    const mappedRoles = _.map(options, ({ label, id }) => {
      return {
        label: roles[id] ? roles[id].label : label,
        id,
        order: roles[id] ? roles[id].order : 15,
        description: roles[id] ? roles[id].description : '',
      };
    });

    const filteredRoles = mappedRoles.filter((role) => !getRolesToOmit().includes(role.label));
    const orderedRoles = _.orderBy(filteredRoles, ['order'], ['asc']);

    let learnMoreLinkArticleId = '';
    let typeOfModal = '';
    let title = '';
    let linkText = '';
    let editRoleMessage = '';

    if (type === 'partner') {
      learnMoreLinkArticleId = '360044740752';
      typeOfModal = 'institution';
      editRoleMessage = _t(
        'If you want to edit their program staff or course role permissions, you can do so in Settings for the respective program or course. '
      );
    } else if (type === 'program') {
      learnMoreLinkArticleId = '360055987132';
      typeOfModal = 'program';
      editRoleMessage = _t(
        'This change affects their program staff role only. If you want to edit their institution staff or course staff role, you can do so in Settings for the respective institution or course. '
      );
    } else if (type === 'course') {
      learnMoreLinkArticleId = '#';
      typeOfModal = 'course';
    }

    linkText = _t('Learn more about #{type} staff roles', { type: typeOfModal });

    if (action === 'edit') {
      title = _t(`Select the #{type} role(s) for staff to update their permissions.`, { type: typeOfModal });
    } else if (action === 'add') {
      title = _t(`2. Select the #{type} staff role(s) you want to give them`, { type: typeOfModal });
      editRoleMessage = '';
    }

    return (
      <div data-e2e={RolesCheckboxGroup.e2eName}>
        <CheckboxGroup
          value={rolesArray}
          onChange={this.onChangeHandler}
          label={title}
          supportText={
            <Typography2 component="p" variant="bodySecondary">
              {editRoleMessage} <PartnerHelpLink linkText={linkText} articleId={learnMoreLinkArticleId} />
            </Typography2>
          }
          css={styles.rolesCheckboxTitle}
        >
          {_.map(orderedRoles, ({ label, description, id }) => {
            return <Checkbox value={id} label={label} supportText={description} />;
          })}
        </CheckboxGroup>
      </div>
    );
  }
}

export const load = (executeAction: $TSFixMe, partnerId: $TSFixMe, type: $TSFixMe) => () => {
  executeAction(actions.rolesOptions.load, { partnerId, type });
};

export const storeConnector = (
  { StaffManagementStoreV2 }: { StaffManagementStoreV2: StaffManagementStoreClass },
  { executeAction }: $TSFixMe
) => {
  const state = StaffManagementStoreV2.get();
  const { partnerId, type } = state;
  const partnerRoleTemplatesState = StaffManagementStoreV2.partnerRoleTemplates();

  return {
    options: _.map(_.get(partnerRoleTemplatesState, 'templates'), (t) => ({
      label: t.name,
      id: t.id,
    })).sort((a, b) => compareByNaturalSortOrder(b.label.toLowerCase(), a.label.toLowerCase())),

    // Handed off to withLoading.
    load: load(executeAction, partnerId, type),
    loading: _.get(partnerRoleTemplatesState, 'initializing') || _.get(partnerRoleTemplatesState, 'loading'),
  };
};

export default compose<Props, Omit<Props, 'error' | 'options'>>(
  withFluxibleContextAsProps(),
  connectToStores<Props, Props & ActionContext>(['StaffManagementStoreV2'], storeConnector),
  withLoadingIndicator()
)(RolesCheckboxGroup);
